/**
 * Los modos para elegir herramienta, en DOS anatomías:
 *
 *  • `cards` — la carátula en 3:2, el nombre y la bajada debajo. Es la del popup de
 *    vertiente de la home (`HomeMinimal`).
 *  • `lista` — la miniatura cuadrada a la izquierda y el nombre + la bajada a su
 *    derecha, en filas sin caja. Es la de la solapa "Herramientas" de la home del
 *    Agente (ADR-2026-09-21-las-herramientas-van-en-lista).
 *
 * Nació adentro del popup y salió a un archivo propio cuando la solapa "Herramientas"
 * pasó a dibujar los mismos modos (ADR-2026-09-20-la-home-de-los-otros-rubros-abre-con-el-agente).
 * **Es UN componente con dos anatomías y no dos componentes** porque lo que las dos
 * superficies comparten no es el estilo sino las DECISIONES, y cada una tiene su ADR: el
 * orden (disponibles primero), qué carátula va, que las de video se reproduzcan, el velo
 * y el candado de lo que el plan no incluye, el pill de "Ver ejemplos" y a dónde va el
 * click de un modo bloqueado. Partido en dos archivos, la próxima vez que una de esas
 * reglas cambie va a cambiar en uno solo.
 *
 * Es PRESENTACIONAL: los modos, su candado y su `onAbrir` entran por props.
 */
import { useMemo, type ComponentType, type ReactNode } from 'react';
import { Clapperboard } from 'lucide-react';
import { EnlaceApp } from '../../../components/EnlaceApp';
import { EstudioVideoCover } from '../../../components/EstudioVideoCover';
import type { ModoHome } from '../../estilos/EstiloMinimal';
import { bajadaCortaModo, caratulaModo, nombreCortoModo } from '../modesCatalog';
import { setupAutoplayVideo } from './HomeCards';

type Anatomia = 'cards' | 'lista';

/** La miniatura de 56 del Estudio: el fondo navy de su carátula con el ícono, sin la
 *  frase, que en ese tamaño no se lee. */
const EstudioVideoMini = () => (
  <span
    aria-hidden="true"
    className="absolute inset-0 grid place-items-center text-[#DCE4FF]"
    style={{ background: 'linear-gradient(160deg,#0A1250 0%,#050933 45%,#02062B 100%)' }}
  >
    <Clapperboard className="h-5 w-5" strokeWidth={2} />
  </span>
);

/**
 * Carátulas DIBUJADAS de lo que no es un modo de `MODES` y no tiene foto propia: el
 * Estudio de Video usa la misma de `/modos`
 * (ADR-2026-09-24-el-estudio-de-video-entra-al-popup-de-la-home). Pintarle el `imagen`
 * de respaldo mostraría la carátula de otro modo.
 */
const CARATULA_DIBUJADA: Record<string, Record<Anatomia, ComponentType>> = {
  'editor-video': { cards: EstudioVideoCover, lista: EstudioVideoMini },
};

/**
 * La carátula, con el velo y el candado del modo bloqueado.
 *
 * La misma pieza en las dos anatomías, con los tamaños del candado adentro: en la card
 * es una foto de 3:2 y el círculo mide 44px; en la fila es una miniatura de 56 y ese
 * círculo ocupa la miniatura entera, así que va a 26. Los modos que traen carátula en
 * VIDEO se ven en movimiento en las dos
 * (ADR-2026-09-04-el-popup-de-modos-reproduce-las-caratulas-en-video): es la misma
 * carátula que la tira y `/modos` reproducen. Qué modo tiene video lo decide
 * `caratulaModo` y nadie más — la portada del rubro y la pisada de Catálogo vienen sin
 * `video` a propósito, así que ahí sigue cayendo al `<img>`.
 */
function Caratula({
  caratula,
  dibujada: Dibujada,
  bloqueado,
  anatomia,
  children,
}: {
  caratula: { poster: string; video?: string };
  /** Si el modo tiene carátula dibujada (`CARATULA_DIBUJADA`), pisa a la foto. */
  dibujada?: ComponentType;
  bloqueado: boolean;
  anatomia: Anatomia;
  /** Lo que va ENCIMA de la carátula (hoy, el pill de "Ver ejemplos" de la card). */
  children?: ReactNode;
}) {
  const esLista = anatomia === 'lista';
  const medios =
    'absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]';
  return (
    <span
      className={
        esLista
          ? 'relative block h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-[#F2F3F5]'
          : 'relative block aspect-[3/2] overflow-hidden bg-[#F2F3F5]'
      }
    >
      {Dibujada ? (
        <Dibujada />
      ) : caratula.video ? (
        <video
          ref={setupAutoplayVideo}
          src={caratula.video}
          poster={caratula.poster || undefined}
          autoPlay
          loop
          muted
          playsInline
          // `metadata`: puede haber más de un video y la lista se dibuja de golpe; el
          // póster cubre el hueco hasta el primer cuadro.
          preload="metadata"
          aria-hidden="true"
          className={medios}
        />
      ) : (
        <img src={caratula.poster} alt="" aria-hidden="true" className={medios} />
      )}
      {/* Bloqueado: velo + candado, la misma lectura que en la tira
          (ADR-2026-08-03-velo-mas-suave-en-los-modos-disponibles). */}
      {bloqueado && (
        <>
          <span aria-hidden="true" className="absolute inset-0 bg-black/55" />
          <span
            aria-hidden="true"
            className={`absolute left-1/2 top-1/2 grid -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-black/55 text-white backdrop-blur-sm ${
              esLista ? 'h-[26px] w-[26px]' : 'h-11 w-11'
            }`}
          >
            <span className={`material-symbols-rounded ${esLista ? 'text-[15px]' : 'text-[24px]'}`}>
              lock
            </span>
          </span>
        </>
      )}
      {children}
    </span>
  );
}

/**
 * El pill "Ver ejemplos" del modo bloqueado que TIENE ejemplos cargados
 * (ADR-2026-08-25-pill-ver-ejemplos-en-cards-bloqueadas). El candado solo dice "no
 * podés"; esto dice que la card igual muestra QUÉ hace el modo, que es lo que la vuelve
 * descubrible. Es un `span` y no un `<button>`: la card entera ya es el botón que abre el
 * visor, y anidar botones es HTML inválido.
 *
 * Dónde va cambia con la anatomía y no es un detalle: en la CARD, sobre la foto, abajo a
 * la derecha —el título va afuera, así que esa esquina está libre y es la que queda más
 * cerca de donde sigue leyendo el ojo—. En la FILA la miniatura mide 56px y no le entra
 * nada encima, así que el pill se va al renglón del nombre, que es donde hay lugar.
 */
function PillEjemplos({ anatomia }: { anatomia: Anatomia }) {
  return anatomia === 'lista' ? (
    <span className="shrink-0 rounded-full bg-black/[0.06] px-2 py-0.5 text-[11px] font-bold text-black/55">
      Ver ejemplos
    </span>
  ) : (
    <span className="absolute bottom-2.5 right-2.5 z-[3] inline-flex items-center rounded-full bg-white/90 px-3 py-1.5 text-[12px] font-bold text-brand-black shadow-lg backdrop-blur">
      Ver ejemplos
    </span>
  );
}

export function GrillaDeModos({
  modos,
  rubroMarca,
  onAbrir,
  anatomia = 'cards',
  className = 'grid grid-cols-2 gap-3 sm:grid-cols-3',
  alFinal,
  caratulaPropia,
  uiNueva = false,
}: {
  modos: ModoHome[];
  rubroMarca: string | null;
  onAbrir: (m: ModoHome) => void;
  /** `cards` (el popup de vertiente) o `lista` (la solapa Herramientas). */
  anatomia?: Anatomia;
  /** El contenedor en sí: cuántas columnas y cuánto aire. Lo decide quien lo monta. */
  className?: string;
  /**
   * Celdas que van DESPUÉS de los modos, en la misma grilla: hoy, las cards de
   * "Próximamente" del popup de la home, que no son modos y no se abren. Sin esta prop
   * la grilla es la de siempre.
   */
  alFinal?: ReactNode;
  /**
   * Una carátula que PISA a la del catálogo para un modo, o `undefined` para dejar la de
   * siempre. Hoy, el popup de la home: cada sección muestra sus carátulas con el fondo de
   * su tinte (ADR-2026-10-05-caratulas-con-el-tinte-de-su-seccion). Sin esta prop, la
   * grilla es la de siempre.
   */
  caratulaPropia?: (path: string) => string | undefined;
  /**
   * La grilla es de la interfaz nueva (el popup de la home por destino, los paneles de la
   * sidebar por secciones): los nombres salen de `NOMBRES_UI_NUEVA` ("Recrear"). Sin la
   * prop, los de siempre ("Plantillas") — la home de vertientes y el teléfono
   * (ADR-2026-10-07-plantillas-en-la-vieja-recrear-en-la-nueva).
   */
  uiNueva?: boolean;
}) {
  const esLista = anatomia === 'lista';

  /**
   * Los DISPONIBLES primero y los bloqueados al final
   * (ADR-2026-09-04-el-popup-ordena-los-modos-disponibles-primero).
   *
   * La lista llega en el orden del catálogo, así que en Videos los dos primeros modos
   * —los dos que más se miran— eran los dos que el plan no incluye: se abría mostrando
   * lo que el usuario NO puede hacer. Ordenar acá y no en la lista que arma el llamador
   * deja intactas la cuenta de herramientas y la ilustración de la card de vertiente,
   * que siguen el orden del catálogo.
   *
   * `sort` es estable desde ES2019: dentro de cada grupo se conserva el orden del
   * catálogo, que es el que decide cuál modo va primero.
   */
  const enOrden = useMemo(
    () => [...modos].sort((a, b) => Number(!!a.bloqueado) - Number(!!b.bloqueado)),
    [modos],
  );

  return (
    <div className={className}>
      {enOrden.map((m) => {
        const propia = caratulaPropia?.(m.path);
        const caratula = propia ? { poster: propia } : caratulaModo(m.path, rubroMarca, m.imagen);
        const nombre = nombreCortoModo(m.path, m.titulo, { uiNueva });
        const bajada = bajadaCortaModo(m.path);
        return (
          <EnlaceApp
            key={m.path}
            // El modo bloqueado no lleva `href` porque su click no va al modo, sino a
            // los planes o al visor de ejemplos: sin `href`, `EnlaceApp` vuelve a
            // dibujarse como botón, que es lo correcto para algo que no navega
            // (ADR-2026-09-08-las-cards-de-la-home-tambien-son-links).
            href={m.href}
            onNavegar={() => onAbrir(m)}
            aria-label={
              m.bloqueado
                ? `${nombre} — ${
                    // Lo que el click va a hacer de verdad, que no es lo mismo en los
                    // dos casos (ver `abrirModo` en el llamador).
                    m.conEjemplos ? 'ver ejemplos' : 'ver planes'
                  }`
                : nombre
            }
            className={
              esLista
                ? // Sin tarjeta, como las "Ediciones rápidas" de esta misma home: el
                  // hover pinta un rectángulo apenas más grande que la fila para que se
                  // note qué se está por clickear. Una lista de filas con caja y sombra
                  // vuelve a ser una grilla de cards angostas.
                  'group flex items-center gap-3.5 rounded-2xl p-2 text-left transition-colors hover:bg-black/[0.04]'
                : 'group overflow-hidden rounded-xl border border-black/[0.06] text-left transition-shadow hover:shadow-[0_2px_16px_rgba(0,0,0,0.09)]'
            }
          >
            <Caratula
              caratula={caratula}
              dibujada={CARATULA_DIBUJADA[m.path]?.[anatomia]}
              bloqueado={!!m.bloqueado}
              anatomia={anatomia}
            >
              {/* La card lleva el pill ENCIMA de la foto; la fila, al lado del nombre. */}
              {!esLista && m.bloqueado && m.conEjemplos && <PillEjemplos anatomia={anatomia} />}
            </Caratula>

            <span className={esLista ? 'min-w-0 flex-1' : 'block px-3.5 py-3'}>
              <span className="flex items-center gap-2">
                <span
                  className={`truncate font-semibold text-brand-black/90 ${
                    esLista ? 'text-[14.5px]' : 'text-[14px]'
                  }`}
                >
                  {nombre}
                </span>
                {esLista && m.bloqueado && m.conEjemplos && <PillEjemplos anatomia={anatomia} />}
              </span>
              {/* El ` ` reserva la línea: en la grilla, un modo sin bajada dejaría la
                  card más baja que sus vecinas de la misma fila. */}
              <span className="mt-0.5 block truncate text-[12.5px] leading-snug text-black/50">
                {bajada || ' '}
              </span>
            </span>
          </EnlaceApp>
        );
      })}
      {alFinal}
    </div>
  );
}

export default GrillaDeModos;
